import React, { useState, useEffect } from 'react'
import i18n from 'live/services/i18n'
import WebSettingsCard from 'live/components/ui/admin/web-settings-card'
import ActionToggle from 'live/components/ui/action-toggle'
import globalNotification from 'live/services/global-notification'
const RESOURCE_URL = '/services/plugin-asset-aggregator/configuration/report'

const Configuration = (): JSX.Element => {
    const [editMode, setEditMode] = useState(false)
    const [saved, setSaved] = useState(false)
    const [loading, setLoading] = useState(true)
    const [configPayload, setConfigPayload] = useState({ debugLogEnabled: true, 
                                                            debugLogSaveToStorage: false, 
                                                            performanceLogEnabled: true, 
                                                            performanceLogSaveToStorage: false,
                                                            infoLogSaveToStorage: true,
                                                            startLogSaveToStorage: true,
                                                            stopLogSaveToStorage: true,
                                                            endHistoryLogSaveToStorage: true,
                                                            traceLogEnabled: false,
                                                            traceLogSaveToStorage: false
                                                        })

    useEffect(() => {
        fetch(RESOURCE_URL)
            .then(resp => {
                return resp.json()
            })
            .then(data => {
                setConfigPayload(data)
            })
            .catch(error => {
                globalNotification.failed(i18n('Failed to get the path', error))
            })
            .finally(() => {
                setLoading(false)
            })
    }, [])

    const onSave = (): void => {
        const headers = {
            'content-type': 'application/json'
        }

        const body = {
            ...configPayload
        }

        setLoading(true)

        fetch(RESOURCE_URL, { method: 'POST', body: JSON.stringify(body), headers })
            .then(resp => {
                if (resp.status !== 200) return Promise.reject(resp)
                setEditMode(false)
                setSaved(true)
                globalNotification.success(i18n('Settings successfully saved'))
            })
            .catch(error => {
                error.json().then(payload => {
                    globalNotification.error(i18n('Failed to save settings') + ': ' + i18n(payload.message))
                })
            })
            .finally(() => {
                setLoading(false)
            })
    }

    return (
        <WebSettingsCard
            name="aggregator-report-config"
            className="live-feature"
            open={editMode}
            error={''}
            saved={saved}
            save={onSave}
            hideButton={loading}
            savedMessage={i18n('Aggregator Configuration settings successfully saved')}
            toggle={(name, status) => {
                setEditMode(status === 'open')
                setSaved(status !== 'open')
            }}
        >
            <h2>{i18n('Report Configuration')}</h2>
            {loading ? <span>{i18n('Loading from server...')}</span> : (
                <>
                    <div className="form-group">
                        <label>{i18n('Trace Log Enabled')}</label>
                        <div className="flex">
                            <div className="flex-1">
                                <ActionToggle
                                    onChange={() => {
                                            setConfigPayload({ ...configPayload, traceLogEnabled: !configPayload.traceLogEnabled })
                                            setSaved(false);
                                        }
                                    }
                                    elLeft={<span>on</span>}
                                    elRight={<span>off</span>}
                                    active={configPayload.traceLogEnabled}
                                    disabled={!editMode}
                                />
                            </div>
                            <div className="flex-1">
                                <div className="flex">
                                    <span className="help-block">
                                        <i className="fa fa-info-circle" />
                                    </span>
                                    <span className="help-block">
                                        <span>
                                            {i18n(
                                                'Will output trace information on the __asset_aggregator stream. Be careful that trace logging is very verbose, only activate for listener or aggregation investigation purposes.'
                                            )}
                                        </span>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="form-group">
                        <label>{i18n('Trace Log Save to Storage')}</label>
                        <div className="flex">
                            <div className="flex-1">
                                <ActionToggle
                                    onChange={() => {
                                            setConfigPayload({ ...configPayload, traceLogSaveToStorage: !configPayload.traceLogSaveToStorage })
                                            setSaved(false);
                                        }
                                    }
                                    elLeft={<span>on</span>}
                                    elRight={<span>off</span>}
                                    active={configPayload.traceLogSaveToStorage}
                                    disabled={!editMode}
                                />
                            </div>
                            <div className="flex-1">
                                <div className="flex">
                                    <span className="help-block">
                                        <i className="fa fa-info-circle" />
                                    </span>
                                    <span className="help-block">
                                        <span>
                                            {i18n(
                                                'Will save the trace events in the stream to the storage. Be careful that trace logging is very verbose, only activate for listener or aggregation investigation purposes.'
                                            )}
                                        </span>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="form-group">
                        <label>{i18n('Debug Log Enabled')}</label>
                        <div className="flex">
                            <div className="flex-1">
                                <ActionToggle
                                    onChange={() => {
                                            setConfigPayload({ ...configPayload, debugLogEnabled: !configPayload.debugLogEnabled })
                                            setSaved(false);
                                        }
                                    }
                                    elLeft={<span>on</span>}
                                    elRight={<span>off</span>}
                                    active={configPayload.debugLogEnabled}
                                    disabled={!editMode}
                                />
                            </div>
                            <div className="flex-1">
                                <div className="flex">
                                    <span className="help-block">
                                        <i className="fa fa-info-circle" />
                                    </span>
                                    <span className="help-block">
                                        <span>
                                            {i18n(
                                                'Will output debug information on the __asset_aggregator stream.'
                                            )}
                                        </span>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="form-group">
                        <label>{i18n('Debug Log Save to Storage')}</label>
                        <div className="flex">
                            <div className="flex-1">
                                <ActionToggle
                                    onChange={() => {
                                            setConfigPayload({ ...configPayload, debugLogSaveToStorage: !configPayload.debugLogSaveToStorage })
                                            setSaved(false);
                                        }
                                    }
                                    elLeft={<span>on</span>}
                                    elRight={<span>off</span>}
                                    active={configPayload.debugLogSaveToStorage}
                                    disabled={!editMode}
                                />
                            </div>
                            <div className="flex-1">
                                <div className="flex">
                                    <span className="help-block">
                                        <i className="fa fa-info-circle" />
                                    </span>
                                    <span className="help-block">
                                        <span>
                                            {i18n(
                                                'Will save the debug events in the stream to the storage.'
                                            )}
                                        </span>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="form-group">
                        <label>{i18n('Performance Log Enabled')}</label>
                        <div className="flex">
                            <div className="flex-1">
                                <ActionToggle
                                    onChange={() => {
                                            setConfigPayload({ ...configPayload, performanceLogEnabled: !configPayload.performanceLogEnabled })
                                            setSaved(false);
                                        }
                                    }
                                    elLeft={<span>on</span>}
                                    elRight={<span>off</span>}
                                    active={configPayload.performanceLogEnabled}
                                    disabled={!editMode}
                                />
                            </div>
                            <div className="flex-1">
                                <div className="flex">
                                    <span className="help-block">
                                        <i className="fa fa-info-circle" />
                                    </span>
                                    <span className="help-block">
                                        <span>
                                            {i18n(
                                                'Will output performance information on the __asset_aggregator stream.'
                                            )}
                                        </span>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="form-group">
                        <label>{i18n('Performance Log Save to Storage')}</label>
                        <div className="flex">
                            <div className="flex-1">
                                <ActionToggle
                                    onChange={() => {
                                            setConfigPayload({ ...configPayload, performanceLogSaveToStorage: !configPayload.performanceLogSaveToStorage })
                                            setSaved(false);
                                        }
                                    }
                                    elLeft={<span>on</span>}
                                    elRight={<span>off</span>}
                                    active={configPayload.performanceLogSaveToStorage}
                                    disabled={!editMode}
                                />
                            </div>
                            <div className="flex-1">
                                <div className="flex">
                                    <span className="help-block">
                                        <i className="fa fa-info-circle" />
                                    </span>
                                    <span className="help-block">
                                        <span>
                                            {i18n(
                                                'Will save the performance events in the stream to the storage.'
                                            )}
                                        </span>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="form-group">
                        <label>{i18n('Info Log Save to Storage')}</label>
                        <div className="flex">
                            <div className="flex-1">
                                <ActionToggle
                                    onChange={() => {
                                            setConfigPayload({ ...configPayload, infoLogSaveToStorage: !configPayload.infoLogSaveToStorage })
                                            setSaved(false);
                                        }
                                    }
                                    elLeft={<span>on</span>}
                                    elRight={<span>off</span>}
                                    active={configPayload.infoLogSaveToStorage}
                                    disabled={!editMode}
                                />
                            </div>
                            <div className="flex-1">
                                <div className="flex">
                                    <span className="help-block">
                                        <i className="fa fa-info-circle" />
                                    </span>
                                    <span className="help-block">
                                        <span>
                                            {i18n(
                                                'Will save the info events in the stream to the storage.'
                                            )}
                                        </span>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="form-group">
                        <label>{i18n('Start Log Save to Storage')}</label>
                        <div className="flex">
                            <div className="flex-1">
                                <ActionToggle
                                    onChange={() => {
                                            setConfigPayload({ ...configPayload, startLogSaveToStorage: !configPayload.startLogSaveToStorage })
                                            setSaved(false);
                                        }
                                    }
                                    elLeft={<span>on</span>}
                                    elRight={<span>off</span>}
                                    active={configPayload.startLogSaveToStorage}
                                    disabled={!editMode}
                                />
                            </div>
                            <div className="flex-1">
                                <div className="flex">
                                    <span className="help-block">
                                        <i className="fa fa-info-circle" />
                                    </span>
                                    <span className="help-block">
                                        <span>
                                            {i18n(
                                                'Will save the start events in the stream to the storage.'
                                            )}
                                        </span>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="form-group">
                        <label>{i18n('Stop Log Save to Storage')}</label>
                        <div className="flex">
                            <div className="flex-1">
                                <ActionToggle
                                    onChange={() => {
                                            setConfigPayload({ ...configPayload, stopLogSaveToStorage: !configPayload.stopLogSaveToStorage })
                                            setSaved(false);
                                        }
                                    }
                                    elLeft={<span>on</span>}
                                    elRight={<span>off</span>}
                                    active={configPayload.stopLogSaveToStorage}
                                    disabled={!editMode}
                                />
                            </div>
                            <div className="flex-1">
                                <div className="flex">
                                    <span className="help-block">
                                        <i className="fa fa-info-circle" />
                                    </span>
                                    <span className="help-block">
                                        <span>
                                            {i18n(
                                                'Will save the stop events in the stream to the storage.'
                                            )}
                                        </span>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="form-group">
                        <label>{i18n('EndHistory Log Save to Storage')}</label>
                        <div className="flex">
                            <div className="flex-1">
                                <ActionToggle
                                    onChange={() => {
                                            setConfigPayload({ ...configPayload, endHistoryLogSaveToStorage: !configPayload.endHistoryLogSaveToStorage })
                                            setSaved(false);
                                        }
                                    }
                                    elLeft={<span>on</span>}
                                    elRight={<span>off</span>}
                                    active={configPayload.endHistoryLogSaveToStorage}
                                    disabled={!editMode}
                                />
                            </div>
                            <div className="flex-1">
                                <div className="flex">
                                    <span className="help-block">
                                        <i className="fa fa-info-circle" />
                                    </span>
                                    <span className="help-block">
                                        <span>
                                            {i18n(
                                                'Will save the "end history" events in the stream to the storage.'
                                            )}
                                        </span>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </WebSettingsCard>
    )
}

export default {
    name: i18n('Configuration'),
    key: 'aggregator-report-config',
    section: i18n('Asset Aggregator Settings'),
    configComponent: Configuration
}
